import * as React from "react";
import { Chart, useChart } from "@chakra-ui/charts";
import { Box, Card, HStack, Stack, Tabs, Text } from "@chakra-ui/react";
import { Bar, BarChart, LabelList, Tooltip, XAxis, YAxis } from "recharts";
import AppAvatar from "components/utils/AppAvatar";
import { getAvatarHexColor, resolveAvatarUrl } from "utils/avatar";

const MODES = {
  socios: {
    label: "Socios",
    title: "Asistencia de actividades",
    subtitle: "Socios presentes por actividad",
    dataKey: "sociosCount",
    empty: "Sin asistencia de socios registrada",
    color: "arawaks.tropicalTeal",
  },
  instructores: {
    label: "Instructores",
    title: "Asistencia de actividades",
    subtitle: "Instructores presentes por actividad",
    dataKey: "instructoresCount",
    empty: "Sin asistencia de instructores registrada",
    color: "arawaks.grapePurple",
  },
};

function shortName(name) {
  return name.length > 18 ? `${name.slice(0, 17)}...` : name;
}

function normalizeAttendee(row) {
  const id = row?.id || row?.socioId || row?.instructorId || row?.nombre;
  const nombre = row?.nombre || row?.socioNombre || row?.instructorNombre || "Persona";
  return {
    id,
    nombre,
    avatarSrc: resolveAvatarUrl(row) || row?.fotoUrl || null,
    avatarColor: getAvatarHexColor(id || nombre),
  };
}

function AttendanceTooltip({ active, payload, mode }) {
  const activity = payload?.[0]?.payload;
  if (!active || !activity) return null;

  const attendees = mode === "instructores" ? activity.instructores : activity.socios;

  return (
    <Box
      bg="dashboard.bg.surface"
      borderWidth="1px"
      borderColor="dashboard.border.default"
      rounded="xl"
      boxShadow="xl"
      px="3"
      py="3"
      minW="240px"
      maxW="320px"
    >
      <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textTransform="uppercase">
        {activity.name}
      </Text>
      <Stack gap="2" mt="2" maxH="260px" overflowY="auto" pr="1">
        {attendees.length ? (
          attendees.map((item) => (
            <HStack key={item.id || item.nombre} gap="2.5" minW="0">
              <AppAvatar name={item.nombre} src={item.avatarSrc} color={item.avatarColor} size={28} />
              <Text fontSize="sm" color="dashboard.fg.default" fontWeight="700" lineHeight="1.2" truncate>
                {item.nombre}
              </Text>
            </HStack>
          ))
        ) : (
          <Text fontSize="sm" color="dashboard.fg.muted" fontWeight="600">
            Sin registros
          </Text>
        )}
      </Stack>
    </Box>
  );
}

function EmptyState({ children }) {
  return (
    <Box
      minH="320px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      borderWidth="1px"
      borderColor="dashboard.border.muted"
      rounded="card"
      bg="dashboard.bg.surfaceMuted"
    >
      <Text color="dashboard.fg.muted" fontSize="sm">
        {children}
      </Text>
    </Box>
  );
}

function ActivityAttendanceContent({ data, mode }) {
  const currentMode = MODES[mode] ? mode : "socios";
  const modeConfig = MODES[currentMode];
  const chartData = React.useMemo(() => {
    return [...data]
      .map((activity) => {
        const socios = (Array.isArray(activity.asistenciaSocios) ? activity.asistenciaSocios : []).map(normalizeAttendee);
        const instructores = (Array.isArray(activity.asistenciaInstructores) ? activity.asistenciaInstructores : []).map(normalizeAttendee);
        const sociosCount = Math.max(
          socios.length,
          Number(activity.asistenciaSociosCount ?? activity.sociosCount ?? activity.socios ?? 0),
        );
        const instructoresCount = Math.max(
          instructores.length,
          Number(activity.asistenciaInstructoresCount ?? activity.instructoresCount ?? activity.instructores ?? 0),
        );

        return {
          id: activity.id,
          name: activity.nombre?.trim() || "Actividad sin nombre",
          shortName: shortName(activity.nombre?.trim() || "Actividad"),
          socios,
          instructores,
          sociosCount,
          instructoresCount,
        };
      })
      .sort((a, b) => Number(b[modeConfig.dataKey] ?? 0) - Number(a[modeConfig.dataKey] ?? 0) || a.name.localeCompare(b.name))
      .slice(0, 10);
  }, [data, modeConfig.dataKey]);

  const total = chartData.reduce((sum, row) => sum + Number(row[modeConfig.dataKey] ?? 0), 0);
  const chart = useChart({
    data: chartData,
    series: [{ name: modeConfig.dataKey, color: modeConfig.color }],
  });
  const series = chart.getSeries({ name: modeConfig.dataKey });

  if (chartData.length === 0 || total === 0) {
    return <EmptyState>{modeConfig.empty}</EmptyState>;
  }

  return (
    <Chart.Root height="320px" chart={chart}>
      <BarChart data={chart.data} layout="vertical" margin={{ top: 8, right: 28, bottom: 8, left: 8 }}>
        <XAxis type="number" allowDecimals={false} axisLine={false} tickLine={false} />
        <YAxis
          type="category"
          dataKey={chart.key("shortName")}
          width={132}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fontWeight: 700, fill: "var(--chakra-colors-dashboard-fg-muted)" }}
        />
        <Tooltip cursor={{ fill: "var(--chakra-colors-dashboard-bg-surface-muted)" }} content={<AttendanceTooltip mode={currentMode} />} />
        <Bar dataKey={chart.key(modeConfig.dataKey)} fill={chart.color(series?.color)} radius={[0, 10, 10, 0]} maxBarSize={34}>
          <LabelList
            dataKey={chart.key(modeConfig.dataKey)}
            position="right"
            fontSize={12}
            fontWeight={800}
            fill="var(--chakra-colors-dashboard-fg-default)"
          />
        </Bar>
      </BarChart>
    </Chart.Root>
  );
}

function ActivityAttendanceChart({ data = [] }) {
  const [mode, setMode] = React.useState("socios");
  const currentMode = MODES[mode] ? mode : "socios";

  return (
    <Card.Root bg="dashboard.bg.surface" borderWidth="1px" borderColor="dashboard.border.muted" rounded="card">
      <Tabs.Root
        value={currentMode}
        onValueChange={(e) => {
          if (e.value) setMode(e.value);
        }}
      >
        <Card.Header gap="4" alignItems="stretch">
          <HStack justify="space-between" align="flex-start" gap="4" wrap="wrap">
            <Box>
              <Text fontFamily="display" fontWeight="700" fontSize="lg" color="dashboard.fg.default">
                {MODES[currentMode].title}
              </Text>
              <Text fontSize="sm" color="dashboard.fg.muted">
                {MODES[currentMode].subtitle}
              </Text>
            </Box>

            <Tabs.List bg="dashboard.bg.surfaceMuted" p="1" rounded="lg">
              <Tabs.Trigger value="socios" rounded="md" px="3">
                Socios
              </Tabs.Trigger>
              <Tabs.Trigger value="instructores" rounded="md" px="3">
                Instructores
              </Tabs.Trigger>
              <Tabs.Indicator rounded="md" />
            </Tabs.List>
          </HStack>
        </Card.Header>

        <Card.Body pt="8">
          <Tabs.Content value="socios" p="0">
            <ActivityAttendanceContent data={data} mode="socios" />
          </Tabs.Content>
          <Tabs.Content value="instructores" p="0">
            <ActivityAttendanceContent data={data} mode="instructores" />
          </Tabs.Content>
        </Card.Body>
      </Tabs.Root>
    </Card.Root>
  );
}

export default ActivityAttendanceChart;
